import type { ExpenseScanResult } from "@/schemas/expenses";

/** One line of the expense form, and how a scan fills the form without undoing anyone's typing. */

export interface DraftLine {
  key: string;
  id?: string;
  date: string;
  amount: string;
  description: string;
  files: File[];
  keptReceipts: Array<{ id: string; fileName: string }>;
}

export const fileKey = (f: File) => `${f.name}-${f.size}-${f.lastModified}`;

export const newLine = (): DraftLine => ({
  key: crypto.randomUUID(),
  date: "",
  amount: "",
  description: "",
  files: [],
  keptReceipts: [],
});

/** A line nobody touched does not stop a save. */
export const lineBlank = (l: DraftLine) =>
  l.date === "" && l.amount === "" && l.description.trim() === "" && l.files.length === 0;

const isUntouched = (l: DraftLine) => lineBlank(l) && l.keptReceipts.length === 0;

/**
 * The form's lines after a scan. Scanned charges take the blank lines first and then new ones;
 * a line with anything in it is left alone. Each file goes on the first line it produced, or on
 * the first scanned line when no line named it, or on a line of its own when nothing was read.
 */
export function mergeScanLines(
  lines: readonly DraftLine[],
  scan: ExpenseScanResult,
  files: readonly File[],
  makeLine: () => DraftLine = newLine,
): DraftLine[] {
  const next = [...lines];
  const blanks = next.flatMap((l, i) => (isUntouched(l) ? [i] : []));
  const filled = scan.lines.map((s) => {
    const at = blanks.shift() ?? next.push(makeLine()) - 1;
    const base = next[at] ?? makeLine();
    const line: DraftLine = {
      ...base,
      date: s.date ?? "",
      amount: s.amount === null ? "" : s.amount.toFixed(2),
      description: s.description ?? "",
    };
    next[at] = line;
    return at;
  });
  files.forEach((file, fileIndex) => {
    const own = scan.lines.findIndex((s) => s.fileIndex === fileIndex);
    let at = filled[own === -1 ? 0 : own];
    if (at === undefined) at = blanks.shift() ?? next.push(makeLine()) - 1;
    const base = next[at] ?? makeLine();
    next[at] = { ...base, files: [...base.files, file] };
  });
  return next;
}
